import { Deposit, DepositType, SpecialConditionData } from '@urbanx/agx.campaigns.client';
import { AgxContractDeposit, type ContractDepositModel } from '@urbanx/agx.ui-components2';
import { useEffect } from 'react';
import {
  BALANCE_DEPOSIT_STEP_NAME,
  INITIAL_DEPOSIT_STEP_NAME,
  isDepositComplete,
} from './ContractComplianceHelper';
import { ContractCompliancePageProps } from './ContractCompliancePageProps';

const EMPTY_SPECIAL_CONDITIONS: SpecialConditionData[] = [];

interface ContractComplianceDepositProps extends ContractCompliancePageProps {
  initialDeposit?: Deposit;
  balanceDeposit?: Deposit;
  salePrice?: number;
  specialConditions?: SpecialConditionData[];
  onDepositValidationErrorChange?: (hasValidationError: boolean) => void;
  onDepositsUpdated?: (updated: { initialDeposit?: Deposit; balanceDeposit?: Deposit }) => void;
}

const ContractComplianceDeposit = ({
  balanceDeposit,
  campaign,
  initialDeposit,
  onDepositValidationErrorChange,
  onDepositsUpdated,
  salePrice = 0,
  specialConditions,
  task,
  toggleTaskStep,
}: ContractComplianceDepositProps) => {
  const initialDepositStep = task.taskSteps?.find(
    (step) => step.name === INITIAL_DEPOSIT_STEP_NAME
  );
  const balanceDepositStep = task.taskSteps?.find(
    (step) => step.name === BALANCE_DEPOSIT_STEP_NAME
  );
  const resolvedSpecialConditions = specialConditions ?? EMPTY_SPECIAL_CONDITIONS;

  const isInitialDepositValid = isDepositComplete(initialDeposit, true);
  const isBalanceDepositValid = isDepositComplete(balanceDeposit, false);

  useEffect(() => {
    if (!initialDepositStep?.order) return;

    if ((initialDepositStep.isCompleted ?? false) !== isInitialDepositValid) {
      toggleTaskStep(initialDepositStep.order, isInitialDepositValid);
    }
  }, [
    initialDepositStep?.isCompleted,
    initialDepositStep?.order,
    isInitialDepositValid,
    toggleTaskStep,
  ]);

  useEffect(() => {
    if (!balanceDepositStep?.order) return;

    if ((balanceDepositStep.isCompleted ?? false) !== isBalanceDepositValid) {
      toggleTaskStep(balanceDepositStep.order, isBalanceDepositValid);
    }
  }, [
    balanceDepositStep?.isCompleted,
    balanceDepositStep?.order,
    isBalanceDepositValid,
    toggleTaskStep,
  ]);

  const defaultValue: ContractDepositModel = {
    initialDeposit: {
      ...(initialDeposit ?? { depositType: DepositType.Initial }),
      depositType: DepositType.Initial,
      isDepositSelected: true,
    },
    balanceDeposit: balanceDeposit ?? { depositType: DepositType.Balance },
  };

  return (
    <AgxContractDeposit
      id="contract-compliance-contract-deposit"
      defaultValue={defaultValue}
      validate
      salePrice={salePrice}
      campaignId={campaign.campaignId ?? ''}
      specialConditions={resolvedSpecialConditions}
      onIsValidChange={(isValid) => {
        onDepositValidationErrorChange?.(!isValid);
      }}
      onValueChanged={({ value }) => {
        onDepositsUpdated?.({
          initialDeposit: {
            ...value.initialDeposit,
            depositType: DepositType.Initial,
            isDepositSelected: true,
          },
          balanceDeposit: value.balanceDeposit,
        });
      }}
    />
  );
};

export default ContractComplianceDeposit;
